/**
 * CustomerAccountTab - Account tab content for customer detail modal
 * Shows points packages, subscriptions, and transactions
 * Extracted from CustomersPage.js
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Plus, History, FileDown, Edit, ChevronDown, ChevronUp, Receipt, ShoppingBag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { formatSwiss } from '@/lib/utils';
import InvoiceRelatedDialog from '../dialogs/InvoiceRelatedDialog';
import SellProductDialog from '../dialogs/SellProductDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerAccountTab = ({
  customerDetails,
  pointsDetails,
  recurringSubscriptions,
  creditSubscriptions,
  services,
  handleOpenAddPointsDialog,
  handleOpenHistoryDialog,
  setEditingPackage,
  setSelectedServiceIds,
  setEditServicesDialogOpen,
  setShowSubscriptionHistoryModal,
  setSubscriptionHistory,
  setLoadingHistory,
  handleOpenAddCreditDialog,
  fetchCreditSubscriptions,
  fetchPointsDetails,
}) => {
  const [expandedPackages, setExpandedPackages] = useState({});
  // ePost: Rechnungsversand per Post für DIESEN Kunden (06/2026).
  const [epostEnabled, setEpostEnabled] = useState(!!customerDetails?.invoice_delivery_epost);
  const [epostSaving, setEpostSaving] = useState(false);
  useEffect(() => {
    setEpostEnabled(!!customerDetails?.invoice_delivery_epost);
  }, [customerDetails?.invoice_delivery_epost, customerDetails?.id]);

  const toggleEpost = async (value) => {
    setEpostEnabled(value);
    setEpostSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/epost/customers/${customerDetails.id}/toggle`,
        { enabled: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(value ? 'Rechnungsversand per Post aktiviert' : 'Rechnungsversand per Post deaktiviert');
    } catch (e) {
      setEpostEnabled(!value);
      toast.error('Konnte nicht gespeichert werden');
    } finally {
      setEpostSaving(false);
    }
  };

  // Lokale Status-Overrides nach Kündigen/Pausieren/Fortsetzen, damit die
  // Karte sofort aktualisiert ohne kompletten Reload (06/2026).
  const [subStatusOverrides, setSubStatusOverrides] = useState({});

  const subStatus = (sub) => subStatusOverrides[sub.id] || sub.status;

  // Abo-Verwaltung im Konto-Tab: NUR kündigen/pausieren/fortsetzen —
  // Neuverkauf erfolgt ausschliesslich über Sales (User-Wunsch 06/2026).
  const handleSubAction = async (sub, action) => {
    if (action === 'cancel' && !window.confirm(`Abo "${sub.name}" wirklich kündigen?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/recurring-subscriptions/${sub.id}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newStatus = action === 'cancel' ? 'cancelled' : action === 'pause' ? 'paused' : 'active';
      setSubStatusOverrides((prev) => ({ ...prev, [sub.id]: newStatus }));
      toast.success(action === 'cancel' ? 'Abo gekündigt' : action === 'pause' ? 'Abo pausiert' : 'Abo fortgesetzt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    }
  };

  // 06/2026 Phase 3 — Zahlungsmethode für wiederkehrendes Abo umstellen
  // ("card" ↔ "invoice"). Beim Wechsel auf "card" prüft das Backend ob der
  // Kunde überhaupt eine gespeicherte Karte hat (siehe subscriptions_routes).
  const [billingMethodOverrides, setBillingMethodOverrides] = useState({});
  const billingMethod = (sub) =>
    billingMethodOverrides[sub.id] || sub.auto_billing_method || 'invoice';

  const handleTogglePaymentMethod = async (sub) => {
    const current = billingMethod(sub);
    const next = current === 'card' ? 'invoice' : 'card';
    if (!window.confirm(
      next === 'card'
        ? 'Auf Auto-Charge (Kreditkarte) umstellen?\n\nVoraussetzung: Kunde hat eine gespeicherte Karte. Sonst fällt das System bei der nächsten Abbuchung auf Rechnung zurück.'
        : 'Auf Rechnung umstellen?\n\nKunde erhält ab nächstem 25. eine QR-Rechnung per Email/WhatsApp.'
    )) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/subscriptions/admin/customer-subscriptions/${sub.id}/payment-method`,
        { auto_billing_method: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBillingMethodOverrides((prev) => ({ ...prev, [sub.id]: next }));
      toast.success(next === 'card' ? '💳 Auto-Charge aktiviert' : '📄 Rechnung aktiviert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Umstellung fehlgeschlagen');
    }
  };

  // 06/2026 — Test-Renewal jetzt auslösen (für Admin-Tests ohne auf den
  // 25. warten zu müssen). Ruft den Backend-Endpoint
  // `/recurring-subscriptions/{id}/trigger-renewal-now` auf.
  const handleTriggerRenewalNow = async (sub) => {
    if (!window.confirm(
      '⚠️ Test-Renewal jetzt auslösen?\n\n' +
      'Es wird sofort eine neue QR-Rechnung erstellt + Punkte gutgeschrieben + ' +
      'Email/WhatsApp an den Kunden verschickt. Nur zum Testen verwenden!'
    )) return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/recurring-subscriptions/${sub.id}/trigger-renewal-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(data.message || 'Test-Renewal ausgelöst');
      if (fetchPointsDetails) fetchPointsDetails();
      if (fetchCreditSubscriptions) fetchCreditSubscriptions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test-Renewal fehlgeschlagen');
    }
  };

  const packageTransactionsMap = useMemo(() => {
    const map = new Map();
    const transactions = pointsDetails?.recent_transactions || [];
    transactions.forEach((tx) => {
      const key = tx.package_id || tx.package_name;
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(tx);
    });
    return map;
  }, [pointsDetails?.recent_transactions]);

  const packages = useMemo(() => pointsDetails?.packages || [], [pointsDetails?.packages]);

  // Nur ECHTE Credit-Abos (fixe Credit-Anzahl) in der "Credit-Abos"-Sektion
  // anzeigen. Punkte-Pakete werden zwar ebenfalls in der subscriptions-
  // Collection abgelegt (credits_total = null/0), gehören aber oben zu den
  // "Punktepaketen" — sonst erscheint jeder Punktekauf doppelt und als "0/0".
  const realCreditSubscriptions = useMemo(
    () => (creditSubscriptions || []).filter((s) => (s.credits_total || 0) > 0),
    [creditSubscriptions]
  );

  const togglePackageExpansion = useCallback((pkgId) => {
    setExpandedPackages(prev => ({
      ...prev,
      [pkgId]: !prev[pkgId]
    }));
  }, []);

  const totalPoints = pointsDetails?.total_points || 0;

  // Handle PDF export for package history
  const handleExportPackagePdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/customers/${customerDetails.id}/package-history/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = `${customerDetails?.first_name}_${customerDetails?.last_name}`.replace(/\s/g, '_');
      link.setAttribute('download', `Paket_Historie_${safeName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF erfolgreich heruntergeladen');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Fehler beim PDF-Export');
    }
  };

  // Handle viewing subscription history (Iter 06/2026 — User Markus:
  // Timeline-Endpoint liefert Rechnungen + Termine + Trainer + Status)
  const handleViewSubscriptionHistory = async (sub) => {
    try {
      setLoadingHistory(true);
      setShowSubscriptionHistoryModal(true);

      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/subscriptions/${sub.id}/timeline`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscriptionHistory(response.data);
    } catch (error) {
      console.error('Error loading subscription timeline:', error);
      toast.error('Fehler beim Laden der Abo-Übersicht');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Iter 06/2026 — User-Wunsch: Rechnungen im Kundenprofil sichtbar +
  // klickbar (→ zeigt verknüpftes Abo + Termine).
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [sellProductOpen, setSellProductOpen] = useState(false);
  const [pendingProductSales, setPendingProductSales] = useState([]);

  const loadPendingProductSales = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/products/pending-sales/${cid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPendingProductSales(res.data || []);
    } catch (e) { /* nicht kritisch */ }
  }, []);

  const cancelPendingSale = async (saleId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/products/sales/${saleId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success(res.data.message || 'Posten storniert');
      loadPendingProductSales(customerDetails?.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stornieren fehlgeschlagen');
    }
  };

  useEffect(() => {
    if (customerDetails?.id) loadPendingProductSales(customerDetails.id);
  }, [customerDetails?.id, loadPendingProductSales]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceReloadKey, setInvoiceReloadKey] = useState(0);

  const customerIdForInvoices =
    customerDetails?.customer_id || customerDetails?.id;

  useEffect(() => {
    if (!customerIdForInvoices) return;
    let active = true;
    setLoadingInvoices(true);
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/invoices`, {
        params: { customer_id: customerIdForInvoices, limit: 20 },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.invoices || [];
        setCustomerInvoices(list);
      })
      .catch((err) => {
        console.error('invoice load err', err);
      })
      .finally(() => active && setLoadingInvoices(false));
    return () => {
      active = false;
    };
  }, [customerIdForInvoices, invoiceReloadKey]);

  const openInvoiceDialog = useCallback((inv) => {
    setSelectedInvoiceId(inv.id || inv.invoice_number);
    setInvoiceDialogOpen(true);
  }, []);

  // CHF-Guthaben-Verlauf (User-Wunsch 06/2026: "Brauche eine History,
  // wie das Geld zusammengekommen ist") — lazy load beim Aufklappen
  const [creditHistoryOpen, setCreditHistoryOpen] = useState(false);
  const [creditHistory, setCreditHistory] = useState(null);
  const [loadingCreditHistory, setLoadingCreditHistory] = useState(false);

  const CREDIT_TYPE_LABELS = {
    credit_added: 'Manuelle Gutschrift',
    refund: 'Erstattung',
    refund_cancellation: 'Termin-Stornierung',
    refund_reversal: 'Erstattung zurückgezogen',
    manual_adjustment: 'Manuelle Korrektur',
    debit: 'Abbuchung',
    invoice_credit: 'Mit Rechnung verrechnet',
  };

  // Admin-Korrektur: Punkte + CHF-Guthaben direkt bearbeiten
  // (User-Wunsch 06/2026: "Auch die Punkte muss ich als Admin bearbeiten
  // können und auch das Guthaben.")
  const [editTarget, setEditTarget] = useState(null); // 'points' | 'credit' | null
  const [editValue, setEditValue] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editMode, setEditMode] = useState('absolute'); // 'absolute' | 'delta'
  const [editCurrent, setEditCurrent] = useState(0);
  const [pointsOverride, setPointsOverride] = useState(null);
  const [creditOverride, setCreditOverride] = useState(null);

  useEffect(() => {
    // Overrides zurücksetzen wenn ein anderer Kunde geöffnet wird
    setPointsOverride(null);
    setCreditOverride(null);
  }, [customerIdForInvoices]);

  const openEditDialog = (target, currentValue) => {
    setEditTarget(target);
    setEditValue(String(currentValue));
    setEditReason('');
    setEditMode('absolute');
    setEditCurrent(Number(currentValue) || 0);
  };

  const handleSaveAdjustment = async () => {
    let value;
    if (editMode === 'delta') {
      const delta = parseFloat(editValue);
      if (isNaN(delta) || delta === 0) {
        toast.error('Bitte eine Differenz eingeben (z.B. -3 oder +5)');
        return;
      }
      value = editCurrent + delta;
      if (value < 0) {
        toast.error(`Abzug zu gross: aktuelles Guthaben ist ${editCurrent}`);
        return;
      }
    } else {
      value = parseFloat(editValue);
      if (isNaN(value) || value < 0) {
        toast.error('Bitte einen gültigen Wert (≥ 0) eingeben');
        return;
      }
    }
    if (!editReason.trim()) {
      toast.error('Bitte einen Grund angeben');
      return;
    }
    setEditSaving(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = editTarget === 'points' ? 'adjust-points' : 'adjust-credit';
      const res = await axios.post(
        `${API}/customers/${customerIdForInvoices}/${endpoint}`,
        { new_balance: value, reason: editReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (editTarget === 'points') {
        setPointsOverride(res.data.new_balance);
        // Paket-Karten neu laden, damit auch "Manuelle Punkte" den
        // korrigierten Wert zeigt (Bug 06/2026)
        fetchPointsDetails?.(customerIdForInvoices);
        toast.success(`Punktestand auf ${res.data.new_balance} gesetzt`);
      } else {
        setCreditOverride(res.data.new_balance);
        setCreditHistory(null); // Verlauf beim nächsten Öffnen neu laden
        toast.success(`Guthaben auf CHF ${Number(res.data.new_balance).toFixed(2)} gesetzt`);
      }
      setEditTarget(null);
    } catch (e) {
      console.error('adjust error', e);
      toast.error(e?.response?.data?.detail || 'Korrektur fehlgeschlagen');
    } finally {
      setEditSaving(false);
    }
  };

  const toggleCreditHistory = async () => {
    if (creditHistoryOpen) {
      setCreditHistoryOpen(false);
      return;
    }
    setCreditHistoryOpen(true);
    if (creditHistory) return;
    setLoadingCreditHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/customers/${customerIdForInvoices}/credit-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreditHistory(res.data);
    } catch (e) {
      console.error('credit history err', e);
      toast.error('Fehler beim Laden des Guthaben-Verlaufs');
    } finally {
      setLoadingCreditHistory(false);
    }
  };

  // Punkte-Verlauf (User-Wunsch 06/2026: "Ich muss nachvollziehen können,
  // woher die Punkte des Kunden kommen") — lazy load beim Aufklappen
  const [pointsHistoryOpen, setPointsHistoryOpen] = useState(false);
  const [pointsHistory, setPointsHistory] = useState(null);
  const [loadingPointsHistory, setLoadingPointsHistory] = useState(false);

  const POINTS_TYPE_LABELS = {
    purchase: 'Kauf / Gutschrift',
    booking: 'Check-in / Buchung',
    manual_adjustment: 'Manuelle Korrektur',
    refund: 'Rückerstattung',
    expiry: 'Punkte verfallen',
  };

  const togglePointsHistory = async () => {
    if (pointsHistoryOpen) {
      setPointsHistoryOpen(false);
      return;
    }
    setPointsHistoryOpen(true);
    if (pointsHistory) return;
    setLoadingPointsHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/customers/${customerIdForInvoices}/points-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPointsHistory(res.data);
    } catch (e) {
      console.error('points history err', e);
      toast.error('Fehler beim Laden des Punkte-Verlaufs');
    } finally {
      setLoadingPointsHistory(false);
    }
  };

  // 📦 Paket Ablaufdatum + manuelle (De-)Aktivierung (User-Wunsch 06/2026)
  const [expiryEditPkg, setExpiryEditPkg] = useState(null);
  const [expiryValue, setExpiryValue] = useState('');
  const [expirySaving, setExpirySaving] = useState(false);

  const refreshAfterPackageAction = () => {
    fetchPointsDetails?.(customerIdForInvoices);
    setPointsOverride(null);
    setPointsHistory(null);
  };

  const handleTogglePackageActive = async (pkg) => {
    const isInactive = pkg.package_status === 'deactivated' || pkg.package_status === 'expired';
    const action = isInactive ? 'reactivate' : 'deactivate';
    if (!isInactive && !window.confirm(
      `Paket "${pkg.package_name}" deaktivieren?\n\nDie restlichen ${pkg.remaining_points} Punkte werden vom Konto abgezogen und eingefroren. Durch Reaktivierung kommen sie zurück.`
    )) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/customers/${customerIdForInvoices}/packages/${pkg.package_id}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        isInactive
          ? `Paket reaktiviert (+${res.data.points_restored || 0} Punkte zurück)`
          : `Paket deaktiviert (−${res.data.points_frozen || 0} Punkte eingefroren)`
      );
      refreshAfterPackageAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    }
  };

  const openExpiryDialog = (pkg) => {
    setExpiryEditPkg(pkg);
    setExpiryValue(pkg.expiry_date || '');
  };

  const saveExpiry = async (dateOrNull) => {
    setExpirySaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/customers/${customerIdForInvoices}/packages/${expiryEditPkg.package_id}/expiry`,
        { expiry_date: dateOrNull || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(dateOrNull ? `Ablaufdatum gesetzt: ${new Date(dateOrNull).toLocaleDateString('de-CH')}` : 'Ablaufdatum entfernt');
      setExpiryEditPkg(null);
      refreshAfterPackageAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setExpirySaving(false);
    }
  };

  const expiryWarn = (dateStr) => {
    if (!dateStr) return false;
    const diff = (new Date(dateStr) - new Date()) / 86400000;
    return diff <= 14;
  };

  // Datum robust formatieren — purchase_date fehlt bei Alt-Daten
  // → Fallback auf created_at (Bug 06/2026: "Gekauft: Invalid Date")
  const fmtSubDate = (obj) => {
    const raw = obj?.purchase_date || obj?.purchased_at || obj?.created_at || obj?.start_date;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('de-CH');
  };

  // Verknüpfte Rechnung eines Abos (invoices.subscription_id) als
  // klickbare Zeile auf der Abo-Karte — User-Wunsch 06/2026:
  // "Wenn ich auf das Abo klicke, muss ich sehen welche Rechnung dazu
  // gehört und wann diese bezahlt wurde."
  const renderSubInvoice = (sub) => {
    const inv = customerInvoices.find((i) => i.subscription_id === sub.id);
    if (!inv) return null;
    const isPaid = inv.status === 'paid';
    const isRefunded = inv.status === 'refunded';
    return (
      <button
        type="button"
        onClick={() => openInvoiceDialog(inv)}
        className="mt-2 w-full flex items-center justify-between text-xs bg-white/80 border border-gray-200 rounded-md px-2.5 py-1.5 hover:border-emerald-400 transition"
        data-testid={`sub-invoice-link-${sub.id}`}
      >
        <span className="font-mono text-gray-700">📄 {inv.invoice_number}</span>
        <span className={isPaid ? 'text-emerald-700 font-semibold' : isRefunded ? 'text-zinc-500 font-semibold' : 'text-amber-700 font-semibold'}>
          {isPaid
            ? `bezahlt${inv.paid_at ? ' am ' + new Date(inv.paid_at).toLocaleDateString('de-CH') : ''}`
            : isRefunded
            ? `erstattet${inv.refunded_at ? ' am ' + new Date(inv.refunded_at).toLocaleDateString('de-CH') : ''}`
            : `offen (${inv.status})`}
          {' · CHF ' + Number(inv.total_amount ?? inv.total ?? 0).toFixed(2)}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {/* ePost-Versand pro Kunde */}
      <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="epost-customer-toggle-row">
        <div className="flex items-center gap-2">
          <span className="text-lg">✉️</span>
          <div>
            <div className="font-medium text-sm text-red-900">Rechnungen per ePost/Post versenden</div>
            <div className="text-xs text-red-600">AN = Rechnungen gehen als physischer Brief an die Profiladresse (ersetzt E-Mail/WhatsApp).</div>
          </div>
        </div>
        <Switch
          checked={epostEnabled}
          disabled={epostSaving}
          onCheckedChange={toggleEpost}
          data-testid="epost-customer-toggle"
        />
      </div>

      {/* Points Packages Section */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <h4 className="font-semibold text-gray-700">📦 Punktepakete</h4>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportPackagePdf}
              className="gap-1 text-xs"
              data-testid="export-package-pdf-btn"
            >
              <FileDown size={14} />
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleOpenHistoryDialog} className="gap-1 text-xs" data-testid="account-package-history-btn">
              <History size={14} />
              Alle Transaktionen
            </Button>
            <Button size="sm" onClick={handleOpenAddPointsDialog} className="gap-1 text-xs" data-testid="account-package-sell-btn">
              <Plus size={14} />
              Paket verkaufen
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSellProductOpen(true)}
              className="gap-1 text-xs border-indigo-500 text-indigo-700 hover:bg-indigo-50"
              data-testid="account-sell-product-btn"
            >
              <ShoppingBag size={14} />
              Produkt verkaufen
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenAddCreditDialog}
              className="gap-1 text-xs border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              data-testid="account-add-credit-btn"
            >
              <Plus size={14} />
              CHF-Gutschrift
            </Button>
          </div>
        </div>
        
        {/* 08/2026: Offene Produkt-Posten ("Auf nächste Rechnung") */}
        {pendingProductSales.length > 0 && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="pending-product-sales">
            <p className="text-sm font-semibold text-amber-900 mb-2">
              🛒 Offene Produkt-Posten ({pendingProductSales.length}) — werden mit der nächsten Rechnung verrechnet
            </p>
            <div className="space-y-1.5">
              {pendingProductSales.map((sale, idx) => (
                <div key={sale.id} className="flex items-center justify-between text-sm bg-white rounded-md px-2.5 py-1.5 border border-amber-100" data-testid={`pending-sale-row-${idx}`}>
                  <span className="text-gray-800">
                    {sale.product_name}{(sale.quantity || 1) > 1 ? ` x${sale.quantity}` : ''}
                    <span className="text-gray-400 text-xs ml-2">
                      {sale.created_at ? new Date(sale.created_at).toLocaleDateString('de-CH') : ''}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-medium">CHF {Number(sale.total_price || 0).toFixed(2)}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs text-red-600 hover:bg-red-50"
                      onClick={() => cancelPendingSale(sale.id)}
                      data-testid={`pending-sale-cancel-${idx}`}
                    >
                      Stornieren
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Package Cards */}
        {packages.length > 0 && (
          <div className="space-y-3" data-testid="account-packages-list">
            {packages.map((pkg, idx) => {
              const pkgKey = pkg.package_id || pkg.package_name;
              const packageTransactions = (pkgKey && packageTransactionsMap.get(pkgKey)) || [];
              const isExpanded = expandedPackages[pkg.package_id];
              // Bei manuellen Korrekturen kann remaining > original sein
              // (z.B. "138 von 20 / 690%") — Anzeige sinnvoll begrenzen
              const displayOriginal = Math.max(pkg.original_points || 0, pkg.remaining_points || 0);
              const pctLeft = displayOriginal > 0
                ? Math.min(100, Math.round((pkg.remaining_points / displayOriginal) * 100))
                : 0;
                
              const isInactive = pkg.package_status === 'deactivated' || pkg.package_status === 'expired';
              return (
                <div key={idx} className={`p-4 rounded-xl border-2 shadow-sm ${isInactive ? 'bg-gray-100 border-gray-300' : 'bg-gradient-to-br from-teal-50 to-teal-100 border-teal-300'}`} data-testid={`account-package-${pkg.package_id || idx}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className={`text-lg font-bold mb-1 flex items-center gap-2 ${isInactive ? 'text-gray-600' : 'text-teal-900'}`}>
                        {pkg.package_name}
                        {pkg.package_status === 'expired' && (
                          <Badge className="bg-rose-100 text-rose-700 border border-rose-300 text-[10px]" data-testid={`package-status-expired-${pkg.package_id}`}>Abgelaufen</Badge>
                        )}
                        {pkg.package_status === 'deactivated' && (
                          <Badge className="bg-gray-200 text-gray-700 border border-gray-300 text-[10px]" data-testid={`package-status-deactivated-${pkg.package_id}`}>Deaktiviert</Badge>
                        )}
                      </p>
                      <p className="text-xs text-teal-700 flex items-center gap-1">
                        {pkg.allowed_service_names && pkg.allowed_service_names[0] !== 'Alle Services' ? (
                          <span>🔒 Nur für {pkg.allowed_service_names.join(', ')}</span>
                        ) : (
                          <span>✓ Für alle Services</span>
                        )}
                        {pkg.package_id && (
                          <button
                            onClick={() => {
                              setEditingPackage(pkg);
                              setSelectedServiceIds(pkg.allowed_services || []);
                              setEditServicesDialogOpen(true);
                            }}
                            className="ml-2 text-teal-600 hover:text-teal-800 underline text-xs"
                            data-testid={`account-package-edit-${pkg.package_id || idx}`}
                          >
                            <Edit size={12} className="inline mr-1" />
                            ändern
                          </button>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-3xl font-black ${isInactive ? 'text-gray-400' : 'text-teal-800'}`}>
                        {isInactive ? (pkg.points_frozen || 0) : pkg.remaining_points}
                      </p>
                      <p className={`text-xs ${isInactive ? 'text-gray-500' : 'text-teal-600'}`}>
                        {isInactive ? 'Punkte eingefroren' : `von ${displayOriginal} Punkten`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-2">
                    <Progress 
                      value={pctLeft} 
                      className="h-2 bg-teal-200"
                    />
                    <div className="flex justify-between text-xs text-teal-600 mt-1">
                      <span>Gekauft: {fmtSubDate(pkg) || '–'}</span>
                      <span>{pctLeft}% übrig</span>
                    </div>
                  </div>

                  {/* Ablaufdatum + Aktivieren/Deaktivieren */}
                  {pkg.package_id && (
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs border-t border-teal-200/70 pt-2">
                      <button
                        type="button"
                        onClick={() => openExpiryDialog(pkg)}
                        className={`flex items-center gap-1 hover:underline ${
                          pkg.expiry_date && expiryWarn(pkg.expiry_date) && !isInactive
                            ? 'text-amber-700 font-semibold'
                            : isInactive ? 'text-gray-500' : 'text-teal-700'
                        }`}
                        data-testid={`package-expiry-btn-${pkg.package_id}`}
                      >
                        📅 {pkg.expiry_date
                          ? `Läuft ab: ${new Date(pkg.expiry_date).toLocaleDateString('de-CH')}`
                          : 'Kein Ablaufdatum'}
                        <Edit size={10} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTogglePackageActive(pkg)}
                        className={`px-2.5 py-1 rounded-md font-semibold border transition ${
                          isInactive
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-white/70 border-gray-300 text-gray-600 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700'
                        }`}
                        data-testid={`package-toggle-active-${pkg.package_id}`}
                      >
                        {isInactive ? '▶ Reaktivieren' : '⏸ Deaktivieren'}
                      </button>
                    </div>
                  )}
                  
                  {/* Expandable Transaction History */}
                  {packageTransactions.length > 0 && (
                    <div className="mt-3 border-t border-teal-300 pt-2">
                      <button
                        onClick={() => togglePackageExpansion(pkg.package_id)}
                        className="flex items-center gap-1 text-xs text-teal-700 hover:text-teal-900"
                        data-testid={`account-package-toggle-${pkg.package_id || idx}`}
                      >
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {packageTransactions.length} Transaktionen anzeigen
                      </button>
                      
                      {isExpanded && (
                        <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                          {packageTransactions.map((tx, txIdx) => (
                            <div key={txIdx} className="flex justify-between items-center text-xs bg-white/50 p-2 rounded" data-testid={`account-package-tx-${pkg.package_id || idx}-${txIdx}`}>
                              <div>
                                <span className="font-medium">{tx.description || tx.transaction_type}</span>
                                <span className="text-gray-500 ml-2">
                                  {new Date(tx.created_at).toLocaleDateString('de-CH')}
                                </span>
                              </div>
                              <span className={`font-bold ${tx.points_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.points_change >= 0 ? '+' : ''}{tx.points_change}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* No packages message */}
        {packages.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-gray-500 text-sm" data-testid="account-packages-empty">
            Keine aktiven Punktepakete vorhanden
          </div>
        )}
      </div>

      {/* Total Points Summary */}
      {pointsDetails && (
        <div className="bg-gray-100 rounded-lg p-4" data-testid="account-total-points">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-700 flex items-center gap-2">
              Gesamtguthaben:
              <button
                type="button"
                onClick={() => openEditDialog('points', pointsOverride ?? totalPoints)}
                className="text-gray-400 hover:text-teal-700 transition"
                title="Punktestand korrigieren"
                data-testid="edit-points-btn"
              >
                <Edit size={14} />
              </button>
            </span>
            <span className="text-2xl font-black text-teal-700" data-testid="account-total-points-value">
              {pointsOverride ?? totalPoints} Punkte
            </span>
          </div>

          {/* Punkte-Verlauf (aufklappbar) */}
          <button
            type="button"
            onClick={togglePointsHistory}
            className="mt-2 text-xs font-medium text-teal-700 hover:text-teal-900 flex items-center gap-1"
            data-testid="points-history-toggle"
          >
            <History size={12} />
            {pointsHistoryOpen ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
          </button>

          {pointsHistoryOpen && (
            <div className="mt-2 border-t border-gray-200 pt-2" data-testid="points-history">
              {loadingPointsHistory ? (
                <p className="text-xs text-gray-500 py-2">Laden…</p>
              ) : !pointsHistory || pointsHistory.entries.length === 0 ? (
                <p className="text-xs text-gray-500 py-2 italic">Keine Punkte-Bewegungen vorhanden.</p>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {pointsHistory.entries.map((e) => (
                    <div
                      key={e.id}
                      className="flex justify-between items-start gap-3 text-xs bg-white/70 rounded px-2 py-1.5"
                      data-testid={`points-history-entry-${e.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate">
                          {POINTS_TYPE_LABELS[e.transaction_type] || e.transaction_type}
                          {e.invoice_number ? ` · ${e.invoice_number}` : ''}
                          {e.service_name ? ` · ${e.service_name}` : ''}
                        </p>
                        {e.description && (
                          <p className="text-[11px] text-gray-500 truncate">{e.description}</p>
                        )}
                        <p className="text-[10px] text-gray-400">
                          {formatSwiss(e.created_at, {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <span
                          className={`font-bold ${
                            e.points_amount >= 0 ? 'text-teal-700' : 'text-rose-600'
                          }`}
                        >
                          {e.points_amount >= 0 ? '+' : '−'}{Math.abs(e.points_amount)} P
                        </span>
                        <p className="text-[10px] text-gray-400">Saldo: {e.balance_after}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CHF Credit Balance — wird beim Quick-Checkout auch ohne aktive Pakete
          eingelöst (z.B. Geschenkgutschein, Kulanz-Gutschrift). Vorher wurde
          das Guthaben NUR im Verkaufen-Dialog gezeigt — der Studio-Owner sah
          im Konto-Tab nichts. */}
      {Number(creditOverride ?? customerDetails?.credit_balance) > 0 && (
        <div
          className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-lg p-4"
          data-testid="account-chf-balance"
        >
          <div className="flex justify-between items-center">
            <div>
              <span className="font-semibold text-gray-700 flex items-center gap-2">
                💰 CHF-Guthaben
                <button
                  type="button"
                  onClick={() =>
                    openEditDialog(
                      'credit',
                      Number(creditOverride ?? (customerDetails?.credit_balance || 0)).toFixed(2)
                    )
                  }
                  className="text-gray-400 hover:text-emerald-700 transition"
                  title="Guthaben korrigieren"
                  data-testid="edit-credit-btn"
                >
                  <Edit size={14} />
                </button>
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                Wird automatisch bei Verkäufen verrechnet
              </p>
            </div>
            <span className="text-2xl font-black text-emerald-700" data-testid="account-chf-balance-value">
              CHF {Number(creditOverride ?? customerDetails.credit_balance).toFixed(2)}
            </span>
          </div>

          {/* Guthaben-Verlauf (aufklappbar) */}
          <button
            type="button"
            onClick={toggleCreditHistory}
            className="mt-2 text-xs font-medium text-emerald-700 hover:text-emerald-900 flex items-center gap-1"
            data-testid="chf-balance-history-toggle"
          >
            <History size={12} />
            {creditHistoryOpen ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
          </button>

          {creditHistoryOpen && (
            <div className="mt-2 border-t border-emerald-200 pt-2" data-testid="chf-balance-history">
              {loadingCreditHistory ? (
                <p className="text-xs text-gray-500 py-2">Laden…</p>
              ) : !creditHistory || creditHistory.entries.length === 0 ? (
                <p className="text-xs text-gray-500 py-2 italic">Keine Guthaben-Bewegungen vorhanden.</p>
              ) : (
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {creditHistory.entries.map((e) => (
                    <div
                      key={e.id}
                      className="flex justify-between items-start gap-3 text-xs bg-white/70 rounded px-2 py-1.5"
                      data-testid={`credit-history-entry-${e.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate">
                          {CREDIT_TYPE_LABELS[e.type] || e.type}
                          {e.invoice_number ? ` · ${e.invoice_number}` : ''}
                        </p>
                        {e.reason && (
                          <p className="text-[11px] text-gray-500 truncate">{e.reason}</p>
                        )}
                        <p className="text-[10px] text-gray-400">
                          {formatSwiss(e.created_at, {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                          {e.created_by_name ? ` · ${e.created_by_name}` : ''}
                        </p>
                      </div>
                      <span
                        className={`font-bold shrink-0 ${
                          e.amount >= 0 ? 'text-emerald-700' : 'text-rose-600'
                        }`}
                      >
                        {e.amount >= 0 ? '+' : '−'}CHF {Math.abs(e.amount).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recurring Subscriptions Section */}
      {recurringSubscriptions.length > 0 && (
        <div className="space-y-3" data-testid="account-recurring-subscriptions">
          <h4 className="font-semibold text-gray-700 flex items-center gap-2">
            🔄 Wiederkehrende Abos (Payrexx)
          </h4>
          <div className="space-y-2">
            {recurringSubscriptions.map((sub) => (
              <div 
                key={sub.id} 
                className={`p-4 rounded-xl border-2 ${
                  sub.status === 'active' 
                    ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300' 
                    : sub.status === 'paused'
                    ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300'
                    : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300'
                }`}
                data-testid={`account-recurring-${sub.id}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-lg">{sub.name}</p>
                    <p className="text-sm text-gray-600">
                      {sub.points_per_month} Punkte/Monat • CHF {(Number(sub.price_per_month) || 0).toFixed(2)}
                    </p>
                    {sub.next_billing_date && sub.status === 'active' && (
                      <p className="text-xs text-blue-600 mt-1">
                        📅 Nächste Abbuchung: {new Date(sub.next_billing_date).toLocaleDateString('de-CH')}
                      </p>
                    )}
                  </div>
                  <Badge variant={subStatus(sub) === 'active' ? 'success' : subStatus(sub) === 'paused' ? 'warning' : 'secondary'}>
                    {subStatus(sub) === 'active' ? 'Aktiv' : subStatus(sub) === 'paused' ? 'Pausiert' : 'Gekündigt'}
                  </Badge>
                </div>
                {/* Verknüpfte Rechnung (klickbar) */}
                {renderSubInvoice(sub)}
                {/* 06/2026 Phase 3 — Auto-Charge / Rechnung Status + Toggle */}
                {sub.is_recurring !== false && subStatus(sub) !== 'cancelled' && (
                  <div
                    className="mt-3 pt-2 border-t border-blue-200/60 flex items-center justify-between flex-wrap gap-2"
                    data-testid={`account-recurring-billing-method-${sub.id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {billingMethod(sub) === 'card' ? (
                        <Badge variant="default" className="bg-purple-600 hover:bg-purple-700">
                          💳 Auto-Charge aktiv
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                          📄 Rechnung (QR)
                        </Badge>
                      )}
                      {(sub.failed_charge_attempts || 0) > 0 && (
                        <Badge variant="destructive" data-testid={`account-recurring-failed-${sub.id}`}>
                          ⚠️ {sub.failed_charge_attempts} fehlgeschl. Versuch{sub.failed_charge_attempts === 1 ? '' : 'e'}
                        </Badge>
                      )}
                      {sub.billing_day && (
                        <Badge variant="outline" className="text-xs text-gray-600">
                          📅 Tag {sub.billing_day}.
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs"
                        onClick={() => handleTogglePaymentMethod(sub)}
                        data-testid={`account-recurring-toggle-billing-${sub.id}`}
                      >
                        🔁 Umstellen
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs text-orange-600 hover:text-orange-700"
                        onClick={() => handleTriggerRenewalNow(sub)}
                        data-testid={`account-recurring-trigger-renewal-${sub.id}`}
                        title="Sofort eine neue Rechnung + Punkte auslösen (zum Testen)"
                      >
                        ⚡ Test-Renewal
                      </Button>
                    </div>
                  </div>
                )}
                {/* Verwaltung: nur kündigen/pausieren/fortsetzen — Neuverkauf über Sales */}
                {subStatus(sub) !== 'cancelled' && (
                  <div className="flex gap-2 mt-3 pt-2 border-t border-blue-200/60">
                    {subStatus(sub) === 'active' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSubAction(sub, 'pause')}
                        data-testid={`account-recurring-pause-${sub.id}`}
                      >
                        ⏸️ Pausieren
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSubAction(sub, 'resume')}
                        data-testid={`account-recurring-resume-${sub.id}`}
                      >
                        ▶️ Fortsetzen
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => handleSubAction(sub, 'cancel')}
                      data-testid={`account-recurring-cancel-${sub.id}`}
                    >
                      🗑️ Kündigen
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Credit Subscriptions Section */}
      {realCreditSubscriptions.length > 0 && (
        <div className="space-y-3" data-testid="account-credit-subscriptions">
          <h4 className="font-semibold text-gray-700 flex items-center gap-2">
            🎫 Credit-Abos (Fixe Anzahl)
          </h4>
          <div className="space-y-2">
            {realCreditSubscriptions.map((sub) => {
              const usedCredits = sub.credits_total - sub.credits_remaining;
              const percentUsed = (usedCredits / sub.credits_total) * 100;
              
              return (
                <div 
                  key={sub.id} 
                  className={`p-4 rounded-xl border-2 ${
                    sub.status === 'active' 
                      ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-300' 
                      : sub.status === 'exhausted'
                      ? 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300'
                      : 'bg-gradient-to-br from-red-50 to-red-100 border-red-300'
                  }`}
                  data-testid={`account-credit-${sub.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-bold text-lg">{sub.name}</p>
                      {sub.allowed_service_names && sub.allowed_service_names.length > 0 && (
                        <p className="text-xs text-gray-600">
                          {sub.allowed_service_names[0] === 'Alle Services' 
                            ? '✓ Für alle Services'
                            : `🔒 Nur für: ${sub.allowed_service_names.join(', ')}`
                          }
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black">
                        {sub.credits_remaining}/{sub.credits_total}
                      </p>
                      <Badge variant={sub.status === 'active' ? 'success' : sub.status === 'exhausted' ? 'secondary' : 'destructive'}>
                        {sub.status === 'active' ? 'Aktiv' : sub.status === 'exhausted' ? 'Aufgebraucht' : 'Abgelaufen'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <Progress value={100 - percentUsed} className="h-2 mt-2" />
                  
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">
                      Gekauft: {fmtSubDate(sub) || '–'}
                      {sub.expires_at && ` • Gültig bis: ${new Date(sub.expires_at).toLocaleDateString('de-CH')}`}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewSubscriptionHistory(sub)}
                      className="text-xs"
                      data-testid={`account-credit-history-${sub.id}`}
                    >
                      <History size={12} className="mr-1" />
                      Historie
                    </Button>
                  </div>
                  
                  {/* Verknüpfte Rechnung (klickbar) */}
                  {renderSubInvoice(sub)}

                  {/* Wiederkehrende Abos: hier verwalten (kündigen/pausieren)
                      — Neuverkauf nur über Sales (User-Wunsch 06/2026) */}
                  {sub.is_recurring && subStatus(sub) !== 'cancelled' && (
                    <div className="flex gap-2 mt-3 pt-2 border-t border-green-300/50">
                      {subStatus(sub) === 'active' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSubAction(sub, 'pause')}
                          data-testid={`account-credit-pause-${sub.id}`}
                        >
                          ⏸️ Pausieren
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSubAction(sub, 'resume')}
                          data-testid={`account-credit-resume-${sub.id}`}
                        >
                          ▶️ Fortsetzen
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => handleSubAction(sub, 'cancel')}
                        data-testid={`account-credit-cancel-${sub.id}`}
                      >
                        🗑️ Kündigen
                      </Button>
                    </div>
                  )}

                  {/* Monthly Summary for active subscriptions */}
                  {subStatus(sub) === 'active' && (
                    <div className="mt-3 pt-3 border-t border-green-300/50">
                      <p className="text-xs text-gray-600">
                        Status: Aktiv seit {fmtSubDate(sub) || '–'}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Subscriptions Info */}
      {recurringSubscriptions.length === 0 && realCreditSubscriptions.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-gray-500 text-sm" data-testid="account-subscriptions-empty">
          Keine aktiven Abonnements vorhanden
        </div>
      )}

      {/* Iter 06/2026 — Rechnungen mit Klick → Abo + Termine */}
      <div className="space-y-3" data-testid="account-invoices-section">
        <div className="flex justify-between items-center pt-2">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Receipt size={16} />
            Rechnungen ({customerInvoices.length})
          </h4>
          <span className="text-xs text-gray-500">Klick öffnet Paket + Termine</span>
        </div>
        {loadingInvoices ? (
          <div className="text-xs text-gray-500 py-2">Lädt…</div>
        ) : customerInvoices.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center text-gray-500 text-xs">
            Keine Rechnungen vorhanden
          </div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {customerInvoices.slice(0, 20).map((inv) => {
              const total = inv.total_amount ?? inv.total ?? 0;
              const isPaid = inv.status === 'paid';
              const isRefunded = inv.status === 'refunded';
              return (
                <button
                  key={inv.id || inv.invoice_number}
                  type="button"
                  onClick={() => openInvoiceDialog(inv)}
                  className="w-full text-left p-2.5 rounded-md border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition flex justify-between items-center"
                  data-testid={`account-invoice-${inv.id || inv.invoice_number}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">
                      {inv.invoice_number}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {inv.issue_date
                        ? new Date(inv.issue_date).toLocaleDateString('de-CH')
                        : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-gray-900 text-sm">
                      CHF {Number(total).toFixed(2)}
                    </p>
                    <Badge
                      className={
                        isPaid
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]'
                          : isRefunded
                          ? 'bg-zinc-100 text-zinc-600 border-zinc-300 text-[10px]'
                          : 'bg-amber-50 text-amber-700 border-amber-300 text-[10px]'
                      }
                    >
                      {isPaid ? 'bezahlt' : isRefunded ? 'erstattet' : inv.status === 'pending' ? 'offen' : inv.status}
                    </Badge>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <SellProductDialog
        open={sellProductOpen}
        onOpenChange={setSellProductOpen}
        customer={customerDetails}
        onSold={() => {
          setInvoiceReloadKey((k) => k + 1);
          loadPendingProductSales(customerDetails?.id);
        }}
      />

      <InvoiceRelatedDialog
        open={invoiceDialogOpen}
        onOpenChange={setInvoiceDialogOpen}
        invoiceId={selectedInvoiceId}
        onChanged={() => {
          setInvoiceReloadKey((k) => k + 1);
          // Abos neu laden — erstattetes/gekündigtes Abo soll sofort
          // aus der Liste verschwinden (Bug 06/2026)
          if (fetchCreditSubscriptions && customerIdForInvoices) {
            fetchCreditSubscriptions(customerIdForInvoices);
          }
        }}
      />

      {/* Admin-Korrektur: Punkte / CHF-Guthaben bearbeiten */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="adjust-balance-dialog">
          <DialogHeader>
            <DialogTitle>
              {editTarget === 'points' ? 'Punktestand korrigieren' : 'CHF-Guthaben korrigieren'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Modus: Neuer Stand vs. ± Differenz (User-Wunsch 06/2026:
                "als Admin muss ich auch minus Punkte machen") */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => { setEditMode('absolute'); setEditValue(String(editCurrent)); }}
                className={`flex-1 text-xs font-medium rounded-md py-1.5 transition ${
                  editMode === 'absolute' ? 'bg-white shadow text-teal-800' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="adjust-mode-absolute"
              >
                Neuer Stand
              </button>
              <button
                type="button"
                onClick={() => { setEditMode('delta'); setEditValue(''); }}
                className={`flex-1 text-xs font-medium rounded-md py-1.5 transition ${
                  editMode === 'delta' ? 'bg-white shadow text-teal-800' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="adjust-mode-delta"
              >
                ± Differenz
              </button>
            </div>
            <div>
              <Label htmlFor="adjust-value">
                {editMode === 'delta'
                  ? (editTarget === 'points' ? 'Differenz (z.B. -3 oder +5)' : 'Differenz in CHF (z.B. -20 oder +50)')
                  : (editTarget === 'points' ? 'Neuer Punktestand' : 'Neues Guthaben (CHF)')}
              </Label>
              <Input
                id="adjust-value"
                type="number"
                min={editMode === 'delta' ? undefined : '0'}
                step={editTarget === 'points' ? '1' : '0.05'}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder={editMode === 'delta' ? '-3' : undefined}
                data-testid="adjust-value-input"
              />
              {editMode === 'delta' && editValue !== '' && !isNaN(parseFloat(editValue)) && (
                <p className="text-xs mt-1 font-medium text-teal-700" data-testid="adjust-delta-preview">
                  {editCurrent} → {Math.max(0, editCurrent + parseFloat(editValue))}
                  {editTarget === 'points' ? ' Punkte' : ' CHF'}
                  {editCurrent + parseFloat(editValue) < 0 && (
                    <span className="text-rose-600"> (Abzug grösser als Guthaben!)</span>
                  )}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="adjust-reason">Grund (Pflicht)</Label>
              <Input
                id="adjust-reason"
                placeholder="z.B. Korrektur nach Erstattung"
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                data-testid="adjust-reason-input"
              />
            </div>
            <p className="text-xs text-gray-500">
              Die Differenz wird als „Manuelle Korrektur" im Verlauf protokolliert.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditTarget(null)}
              disabled={editSaving}
              data-testid="adjust-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSaveAdjustment}
              disabled={editSaving}
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="adjust-save-btn"
            >
              {editSaving ? 'Speichern…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paket-Ablaufdatum bearbeiten */}
      <Dialog open={!!expiryEditPkg} onOpenChange={(o) => !o && setExpiryEditPkg(null)}>
        <DialogContent className="max-w-sm" data-testid="package-expiry-dialog">
          <DialogHeader>
            <DialogTitle>Ablaufdatum — {expiryEditPkg?.package_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="package-expiry-date">Ablaufdatum</Label>
              <Input
                id="package-expiry-date"
                type="date"
                value={expiryValue}
                onChange={(e) => setExpiryValue(e.target.value)}
                data-testid="package-expiry-input"
              />
            </div>
            <p className="text-xs text-gray-500">
              14 Tage vor Ablauf erhält der Kunde eine Push-Erinnerung.
              Am Ablauftag verfallen die Restpunkte automatisch (jederzeit reaktivierbar).
            </p>
          </div>
          <DialogFooter>
            {expiryEditPkg?.expiry_date && (
              <Button
                variant="ghost"
                onClick={() => saveExpiry(null)}
                disabled={expirySaving}
                className="text-rose-600 hover:text-rose-700"
                data-testid="package-expiry-remove-btn"
              >
                Entfernen
              </Button>
            )}
            <Button variant="outline" onClick={() => setExpiryEditPkg(null)} disabled={expirySaving}>
              Abbrechen
            </Button>
            <Button
              onClick={() => saveExpiry(expiryValue)}
              disabled={expirySaving || !expiryValue}
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="package-expiry-save-btn"
            >
              {expirySaving ? 'Speichern…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default React.memo(CustomerAccountTab);
